import { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { logout } from '../auth/auth';
import { getIdClaims, getAccessToken } from '../auth/tokens';
import { config } from '../config';
import { toMessage } from './common';
import logoUrl from '../assets/wabv-logo-navy.svg';

// Chrome for the authenticated app: top bar (user menu, change password) +
// collapsible left sidebar listing valuations. The client upload portal does
// not use this layout; it stays public and chromeless.
export function Layout() {
  const navigate = useNavigate();
  const [userOpen, setUserOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const claims = getIdClaims();
  const displayName = claims.name || claims.email || 'Signed in';

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <>
      <header className="app-header">
        <Link to="/jobs" aria-label="Home">
          <img className="brand-logo" src={logoUrl} alt="WA Business Valuations" />
        </Link>
        <span style={{ flex: 1 }} />
        <div className="user-menu" ref={userRef}>
          <button className="btn-secondary" onClick={() => setUserOpen((v) => !v)}>
            {displayName} ▾
          </button>
          {userOpen ? (
            <div className="menu-pop">
              <button
                onClick={() => {
                  setUserOpen(false);
                  setPwOpen(true);
                }}
              >
                Change password
              </button>
              <button
                onClick={() => {
                  setUserOpen(false);
                  navigate('/settings');
                }}
              >
                Settings
              </button>
              <button onClick={() => logout()}>Sign out</button>
            </div>
          ) : null}
        </div>
      </header>
      <main className="container">
        <Outlet />
      </main>
      {pwOpen ? <ChangePassword onClose={() => setPwOpen(false)} /> : null}
    </>
  );
}

function ChangePassword({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
        },
        body: JSON.stringify({
          PreviousPassword: current,
          ProposedPassword: next,
          AccessToken: getAccessToken(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message || `Change password failed (${res.status})`);
      }
      setDone(true);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal" onClick={(e) => e.stopPropagation()}>
        <h2>Change password</h2>
        {done ? (
          <>
            <p className="muted">Password updated.</p>
            <div className="btn-row">
              <button onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <label>
              Current password
              <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label>
              New password (14 characters minimum)
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                minLength={14}
                required
              />
            </label>
            {error ? <p style={{ color: 'var(--err)' }}>{error}</p> : null}
            <div className="btn-row">
              <button type="submit" disabled={busy}>
                {busy ? 'Updating.' : 'Update password'}
              </button>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
